<script setup lang="ts">
import { Head, router, useHttp } from '@inertiajs/vue3';
import { ref } from 'vue';
import { updateReasons } from '@/actions/App/Http/Controllers/PrismaController';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import type { Project } from '@/types';

interface PrismaStats {
    identification: {
        total: number;
        from_databases: number;
        from_other_sources: number;
    };
    screening: {
        screened: number;
        excluded: number;
        maybe: number;
        pending: number;
    };
    included: number;
    manual_metrics: Record<string, number>;
}

interface Props {
    project: Project;
    stats: PrismaStats;
}

const props = defineProps<Props>();
const toast = useToast();

defineOptions({
    layout: (props: { project: Project }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
                { title: 'Diagramme PRISMA', href: '#' },
            ],
        },
    ],
});

// ─── Édition des métriques manuelles (motifs PRISMA) ────────────────────────────

const metricsDialogOpen = ref(false);
const savingMetrics = ref(false);

const metricsForm = useHttp({
    duplicates_removed: 0,
    external_sources_count: 0,
    excluded_full_text: 0,
});

function openMetricsDialog() {
    metricsForm.duplicates_removed =
        props.stats.manual_metrics.duplicates_removed ?? 0;
    metricsForm.external_sources_count =
        props.stats.manual_metrics.external_sources_count ?? 0;
    metricsForm.excluded_full_text =
        props.stats.manual_metrics.excluded_full_text ?? 0;
    metricsDialogOpen.value = true;
}

async function saveMetrics() {
    savingMetrics.value = true;

    metricsForm.transform(() => ({
        reasons: [
            {
                metric_key: 'duplicates_removed',
                value: Number(metricsForm.duplicates_removed) || 0,
            },
            {
                metric_key: 'external_sources_count',
                value: Number(metricsForm.external_sources_count) || 0,
            },
            {
                metric_key: 'excluded_full_text',
                value: Number(metricsForm.excluded_full_text) || 0,
            },
        ],
    }));

    try {
        await metricsForm.put(updateReasons.url(props.project.id));
        toast.success('Métriques mises à jour.');
        metricsDialogOpen.value = false;
        router.reload({ only: ['stats'] });
    } catch {
        toast.error('Erreur lors de la mise à jour des métriques.');
    } finally {
        savingMetrics.value = false;
    }
}

// ─── Export du diagramme (image / PDF) ──────────────────────────────────────────

const diagramRef = ref<HTMLElement | null>(null);
const exporting = ref(false);

function slugify(value: string): string {
    return value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

/** Rasterise le diagramme en PNG (fond blanc, haute résolution). */
async function renderDiagramPng(): Promise<string | null> {
    if (!diagramRef.value) {
        return null;
    }

    const { toPng } = await import('html-to-image');

    return toPng(diagramRef.value, {
        backgroundColor: '#ffffff',
        pixelRatio: 2,
    });
}

async function exportImage() {
    exporting.value = true;

    try {
        const dataUrl = await renderDiagramPng();

        if (!dataUrl) {
            return;
        }

        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `prisma-${slugify(props.project.name)}.png`;
        link.click();
    } catch {
        toast.error("Erreur lors de l'export en image.");
    } finally {
        exporting.value = false;
    }
}

async function exportPdf() {
    exporting.value = true;

    try {
        const dataUrl = await renderDiagramPng();

        if (!dataUrl) {
            return;
        }

        const { jsPDF } = await import('jspdf');
        const img = new Image();
        img.src = dataUrl;
        await new Promise((resolve) => {
            img.onload = resolve;
        });

        const orientation = img.width >= img.height ? 'landscape' : 'portrait';
        const pdf = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 32;

        const maxWidth = pageWidth - margin * 2;
        const maxHeight = pageHeight - margin * 2;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const renderWidth = img.width * ratio;
        const renderHeight = img.height * ratio;

        pdf.addImage(
            dataUrl,
            'PNG',
            (pageWidth - renderWidth) / 2,
            margin,
            renderWidth,
            renderHeight,
        );
        pdf.save(`prisma-${slugify(props.project.name)}.pdf`);
    } catch {
        toast.error("Erreur lors de l'export en PDF.");
    } finally {
        exporting.value = false;
    }
}
</script>

<template>
    <Head :title="`Diagramme PRISMA — ${project.name}`" />

    <div class="flex-1 overflow-y-auto">
        <div class="mx-auto max-w-3xl px-10 py-12">
            <!-- Page Header -->
            <header class="mb-8 flex items-start justify-between">
                <div>
                    <div
                        class="mb-1 text-xs font-semibold tracking-widest text-muted-foreground uppercase"
                    >
                        Suivi
                    </div>
                    <h1
                        class="text-2xl font-bold tracking-tight text-foreground"
                    >
                        Diagramme PRISMA
                    </h1>
                    <p class="mt-1 text-sm text-muted-foreground">
                        Vue d'ensemble des étapes de sélection — effectifs et
                        motifs d'exclusion sont mis à jour automatiquement.
                    </p>
                </div>
                <div class="flex shrink-0 gap-2 pl-4">
                    <button
                        type="button"
                        :disabled="exporting"
                        class="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                        @click="exportImage"
                    >
                        Exporter en image
                    </button>
                    <button
                        type="button"
                        :disabled="exporting"
                        class="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                        @click="exportPdf"
                    >
                        Exporter en PDF
                    </button>
                </div>
            </header>

            <!-- PRISMA board -->
            <div
                ref="diagramRef"
                class="bg-background p-4"
                data-tour="prisma-diagram"
            >
                <!-- Identification -->
                <div class="flex items-center gap-4">
                    <div
                        class="w-28 shrink-0 text-right text-xs font-semibold text-muted-foreground"
                    >
                        Identification
                    </div>
                    <div class="flex-1 rounded-lg border border-border p-4">
                        <div class="text-xs text-muted-foreground">
                            Articles identifiés dans les bases
                        </div>
                        <div class="mt-1 text-2xl font-bold">
                            {{ stats.identification.from_databases }}
                        </div>
                        <div
                            v-if="stats.identification.from_other_sources > 0"
                            class="mt-0.5 text-xs text-muted-foreground"
                        >
                            +
                            {{ stats.identification.from_other_sources }} depuis
                            d'autres sources
                        </div>
                    </div>
                    <div class="shrink-0 text-xs text-muted-foreground">→</div>
                    <div
                        class="w-52 shrink-0 rounded-lg border border-border p-3"
                    >
                        <div class="text-xs text-muted-foreground">
                            Doublons retirés
                        </div>
                        <div class="mt-1 text-xl font-bold">
                            {{ stats.manual_metrics.duplicates_removed ?? '—' }}
                        </div>
                    </div>
                </div>

                <div class="ml-32 h-6 border-l-2 border-border"></div>

                <!-- Sélection -->
                <div class="flex items-center gap-4">
                    <div
                        class="w-28 shrink-0 text-right text-xs font-semibold text-muted-foreground"
                    >
                        Sélection
                    </div>
                    <div class="flex-1 rounded-lg border border-border p-4">
                        <div class="text-xs text-muted-foreground">
                            Articles examinés (titres et résumés)
                        </div>
                        <div class="mt-1 text-2xl font-bold">
                            {{ stats.screening.screened }}
                        </div>
                        <div class="mt-0.5 text-xs text-muted-foreground">
                            {{ stats.screening.pending }} en attente de décision
                        </div>
                    </div>
                    <div class="shrink-0 text-xs text-muted-foreground">→</div>
                    <div
                        class="w-52 shrink-0 rounded-lg border border-destructive/30 p-3"
                    >
                        <div class="text-xs text-muted-foreground">
                            Articles exclus
                        </div>
                        <div class="mt-1 text-xl font-bold">
                            {{ Math.max(0, stats.screening.excluded) }}
                        </div>
                        <button
                            type="button"
                            class="mt-2 block cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                            @click="openMetricsDialog"
                        >
                            Modifier les motifs
                        </button>
                    </div>
                </div>

                <div class="ml-32 h-6 border-l-2 border-border"></div>

                <!-- Éligibilité -->
                <div class="flex items-center gap-4">
                    <div
                        class="w-28 shrink-0 text-right text-xs font-semibold text-muted-foreground"
                    >
                        Éligibilité
                    </div>
                    <div class="flex-1 rounded-lg border border-border p-4">
                        <div class="text-xs text-muted-foreground">
                            Articles évalués en texte intégral
                        </div>
                        <div class="mt-1 text-2xl font-bold">
                            {{ stats.screening.maybe }}
                        </div>
                    </div>
                    <div class="shrink-0 text-xs text-muted-foreground">→</div>
                    <div
                        class="w-52 shrink-0 rounded-lg border border-destructive/30 p-3"
                    >
                        <div class="text-xs text-muted-foreground">
                            Articles exclus
                        </div>
                        <div class="mt-1 text-xl font-bold">
                            {{
                                stats.manual_metrics.excluded_full_text != null
                                    ? Math.max(
                                          0,
                                          stats.manual_metrics
                                              .excluded_full_text,
                                      )
                                    : '—'
                            }}
                        </div>
                        <button
                            type="button"
                            class="mt-2 block cursor-pointer text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                            @click="openMetricsDialog"
                        >
                            Modifier les motifs
                        </button>
                    </div>
                </div>

                <div class="ml-32 h-6 border-l-2 border-border"></div>

                <!-- Inclusion -->
                <div class="flex items-center gap-4">
                    <div
                        class="w-28 shrink-0 text-right text-xs font-semibold text-muted-foreground"
                    >
                        Inclusion
                    </div>
                    <div
                        class="flex-1 rounded-lg border border-primary/30 bg-primary/5 p-4"
                    >
                        <div class="text-xs text-muted-foreground">
                            Articles inclus dans la revue
                        </div>
                        <div class="mt-1 text-2xl font-bold">
                            {{ stats.included }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Édition des métriques manuelles -->
        <Dialog v-model:open="metricsDialogOpen">
            <DialogContent class="max-w-md">
                <DialogHeader>
                    <DialogTitle>Modifier les métriques manuelles</DialogTitle>
                    <DialogDescription>
                        Renseignez les effectifs qui ne sont pas calculés
                        automatiquement à partir de vos décisions.
                    </DialogDescription>
                </DialogHeader>

                <div class="space-y-4 py-2">
                    <div class="space-y-1.5">
                        <Label for="metric-duplicates">Doublons retirés</Label>
                        <Input
                            id="metric-duplicates"
                            v-model.number="metricsForm.duplicates_removed"
                            type="number"
                            min="0"
                        />
                    </div>
                    <div class="space-y-1.5">
                        <Label for="metric-external"
                            >Articles issus d'autres sources</Label
                        >
                        <Input
                            id="metric-external"
                            v-model.number="metricsForm.external_sources_count"
                            type="number"
                            min="0"
                        />
                    </div>
                    <div class="space-y-1.5">
                        <Label for="metric-fulltext"
                            >Articles exclus en texte intégral</Label
                        >
                        <Input
                            id="metric-fulltext"
                            v-model.number="metricsForm.excluded_full_text"
                            type="number"
                            min="0"
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        type="button"
                        @click="metricsDialogOpen = false"
                    >
                        Annuler
                    </Button>
                    <Button
                        type="button"
                        :disabled="savingMetrics"
                        @click="saveMetrics"
                    >
                        {{ savingMetrics ? 'Enregistrement…' : 'Enregistrer' }}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>
